import React, { useState, useEffect, useCallback } from "react";
import SummaryCard from "../../components/SummaryCard";
import { fetchUserSchedule } from "../../services/users";
import { useUser } from "../../context/UserContext";
import axiosInstance from "../../services/api/axios"; // Import axiosInstance
import {
    CalendarClock,
    Home,
    Users,
    ShoppingCart,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate for navigation
import { Tooltip } from '@material-tailwind/react';

const AgentDashboard = () => {
  const [scheduledCustomers, setScheduledCustomers] = useState([]);
  const user = useUser(); // Assuming you have a UserContext to get user info
  const [visitsTodayCount, setVisitsTodayCount] = useState(0);
  const [totalProjectsCount, setTotalProjectsCount] = useState(0);
  const [interestedCustomersCount, setInterestedCustomersCount] = useState(0); // Placeholder
  const [soldConfigUnitsCount, setSoldConfigUnitsCount] = useState(0);  // Placeholder
  const navigate = useNavigate(); // Hook for navigation

  const fetchData = useCallback(async () => {
    try {
      let agentId = user?.userInfo?.loginIdentifierDetails?.customerId;

      if (!agentId) {
        console.warn("Could not determine agent ID.");
        return;
      }

      const customers = await fetchUserSchedule(null, null, null, agentId); // null values means not considering customer and login,
      setScheduledCustomers(customers || []);

      // Filter for today's visits
      const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
      const todaysVisits = (customers || []).filter(customer =>
        customer.visitSchedule && customer.visitSchedule.slice(0, 10) === today
      );
      setVisitsTodayCount(todaysVisits.length);

      // Fetch total projects using /customers/relation endpoint
      const relationData = await axiosInstance.get("/customers/relation", {
        params: {
          secondaryCustomerId: agentId, // Use agentId
        },
      });
      // Handle both paginated and non-paginated responses
      const relationArray = relationData?.items || (Array.isArray(relationData) ? relationData : []);
      const agentAssignedProjects = relationArray.map((relation) => relation.project);
      setTotalProjectsCount(agentAssignedProjects.length);


      // You'll need to fetch or calculate the other counts (interested customers, sold units) from your API or data source.
      // Replace the placeholders with your actual data fetching logic.
      // setInterestedCustomersCount(await fetchInterestedCustomers(agentId));
      // setSoldConfigUnitsCount(await fetchSoldUnits(agentId));

    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get the agent's name for the welcome message
  const agentName = user?.userInfo?.loginIdentifierDetails?.ownerName || "Agent";
  const today = new Date().toLocaleDateString();

   // Define navigation functions
    const goToScheduleVisit = () => navigate('/schedule-visit'); // Replace '/schedule-visit' with the correct route
    const goToViewProjects = () => navigate('/projects'); // Replace '/projects' with the correct route
    const goToViewConfigurations = () => navigate('/configurations'); // Replace '/configurations' with the correct route
    const goToCustomers = () => navigate('/customers');

  return (
    <div className="max-w-3xl mx-auto px-2 sm:px-4 md:px-8 py-4 md:py-8 animate-fade-in">
      {/* Welcome Message */}
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">Welcome, {agentName}!</h2>
        <h4 className="text-base sm:text-lg text-gray-500 font-medium">Today is {today}</h4>
      </div>

      {/* Summary Cards Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col items-center">
          <CalendarClock className="h-7 w-7 text-flamingo-500 mb-2" />
          <div className="text-2xl font-semibold text-gray-800">{visitsTodayCount}</div>
          <div className="text-sm text-gray-500">Visits Today</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col items-center">
          <Home className="h-7 w-7 text-flamingo-500 mb-2" />
          <div className="text-2xl font-semibold text-gray-800">{totalProjectsCount}</div>
          <div className="text-sm text-gray-500">Total Projects</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col items-center">
          <Users className="h-7 w-7 text-flamingo-500 mb-2" />
          <div className="text-2xl font-semibold text-gray-800">{interestedCustomersCount}</div>
          <div className="text-sm text-gray-500">Interested Customers</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col items-center">
          <ShoppingCart className="h-7 w-7 text-flamingo-500 mb-2" />
          <div className="text-2xl font-semibold text-gray-800">{soldConfigUnitsCount}</div>
          <div className="text-sm text-gray-500">Sold Config Units</div>
        </div>
      </div>

      {/* Quick Actions Section */}
      <div className="flex justify-center gap-6 mb-8">
        <Tooltip content="View Projects" placement="top">
          <button
            className="p-3 rounded-full bg-white shadow hover:bg-flamingo-50 transition"
            onClick={goToViewProjects}
            aria-label="View Projects"
          >
            <Home className="h-6 w-6 text-flamingo-500" />
          </button>
        </Tooltip>
        <Tooltip content="View Customers" placement="top">
          <button
            className="p-3 rounded-full bg-white shadow hover:bg-flamingo-50 transition"
            onClick={goToCustomers}
            aria-label="View Customers"
          >
            <Users className="h-6 w-6 text-flamingo-500" />
          </button>
        </Tooltip>
      </div>

      {/* Scheduled Customers Table */}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 overflow-x-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Today's Scheduled Customers</h3>
        <div className="w-full overflow-x-auto">
          <table className="min-w-full text-xs sm:text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="py-2 px-2 text-left font-medium text-gray-600 whitespace-nowrap">Customer</th>
                <th className="py-2 px-2 text-left font-medium text-gray-600 whitespace-nowrap">Phone</th>
                <th className="py-2 px-2 text-left font-medium text-gray-600 whitespace-nowrap">WhatsApp</th>
                <th className="py-2 px-2 text-left font-medium text-gray-600 whitespace-nowrap">Project</th>
                <th className="py-2 px-2 text-left font-medium text-gray-600 whitespace-nowrap">Date</th>
                <th className="py-2 px-2 text-left font-medium text-gray-600 whitespace-nowrap">Time</th>
                <th className="py-2 px-2 text-left font-medium text-gray-600 whitespace-nowrap">Config</th>
              </tr>
            </thead>
            <tbody>
              {(scheduledCustomers || []).filter(customer => customer.visitSchedule && customer.visitSchedule.slice(0,10) === new Date().toISOString().slice(0,10)).map((customer, index) => (
                <tr key={index} className="hover:bg-flamingo-50 transition">
                  <td className="py-2 px-2 text-gray-700 whitespace-nowrap">{customer.user.firstName} {customer.user.lastName}</td>
                  <td className="py-2 px-2 text-gray-700 whitespace-nowrap">{customer.user.mobileNoCalling}</td>
                  <td className="py-2 px-2 text-gray-700 whitespace-nowrap">{customer.user.mobileNoWa}</td>
                  <td className="py-2 px-2 text-gray-700 whitespace-nowrap">{customer.propertyConfig.project.projectName}</td>
                  <td className="py-2 px-2 text-gray-700 whitespace-nowrap">{new Date(customer.visitSchedule).toLocaleDateString()}</td>
                  <td className="py-2 px-2 text-gray-700 whitespace-nowrap">{new Date(customer.visitSchedule).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="py-2 px-2 text-gray-700 whitespace-nowrap">{customer.propertyConfig.configName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;